import type {
  clearFirstCameraErroredTime,
  setCameraUnavailable,
  setCanEnterWithoutCamera,
  setFirstCameraErroredTime,
  subscribeUGLearningMode,
  unSubscribeUGLearningMode,
  updateTeacherAVState,
} from 'actions/live-class-config';
import type { Reducer } from 'redux';

import { ACTIONS } from 'actions/live-class-config';

export type LearningModeConfigState = {
  didStartUnguidedSheet: boolean;
  sheetType: string;
  sheetMode: string;
  allQuestionsCorrect?: boolean;
};

interface LiveClassConfigState {
  [teacherClassroomId: string]: {
    cameraErroredTime?: number;
    cameraUnavailable?: boolean;
    canEnterWithoutCamera?: boolean;
    learningModeConfig?: LearningModeConfigState;
    teacherAVState?: {
      activeStudentId?: string;
      onHold: boolean;
      onMute: boolean;
    };
  };
}

const initialState: LiveClassConfigState = {};

type LiveClassConfigReducer = Reducer<
  LiveClassConfigState,
  | ReturnType<typeof setFirstCameraErroredTime>
  | ReturnType<typeof clearFirstCameraErroredTime>
  | ReturnType<typeof setCameraUnavailable>
  | ReturnType<typeof setCanEnterWithoutCamera>
  | ReturnType<typeof subscribeUGLearningMode>
  | ReturnType<typeof unSubscribeUGLearningMode>
  | ReturnType<typeof updateTeacherAVState>
>;

const liveClassConfigReducer: LiveClassConfigReducer = (state = initialState, action) => {
  const { type, payload } = action;

  if (type === ACTIONS.SET_FIRST_CAMERA_ERRORED_TIME) {
    const { teacherClassroomId } = payload;

    return {
      [teacherClassroomId]: {
        ...state[teacherClassroomId],
        cameraErroredTime: payload.time,
      },
    };
  }

  if (type === ACTIONS.CLEAR_FIRST_CAMERA_ERRORED_TIME) {
    const { teacherClassroomId } = payload;

    return {
      [teacherClassroomId]: {
        ...state[teacherClassroomId],
        cameraErroredTime: undefined,
        cameraUnavailable: undefined,
      },
    };
  }

  if (type === ACTIONS.SET_CAN_ENTER_WITHOUT_CAMERA) {
    const { teacherClassroomId } = payload;

    return {
      [teacherClassroomId]: {
        ...state[teacherClassroomId],
        canEnterWithoutCamera: true,
      },
    };
  }

  if (type === ACTIONS.SET_CAMERA_UNAVAILABLE) {
    const { teacherClassroomId } = payload;

    return {
      [teacherClassroomId]: {
        ...state[teacherClassroomId],
        cameraUnavailable: true,
      },
    };
  }

  if (type === ACTIONS.SET_LEARNING_MODE) {
    const {
      teacherClassroomId,
      sheet_type: sheetType,
      sheet_mode: sheetMode,
      all_questions_correct: allQuestionsCorrect,
    } = payload;

    const updatedLearningState: LearningModeConfigState = {
      didStartUnguidedSheet: true,
      sheetType,
      sheetMode,
    };

    if (allQuestionsCorrect !== undefined) {
      updatedLearningState.allQuestionsCorrect = allQuestionsCorrect;
    }

    return {
      [teacherClassroomId]: {
        ...state[teacherClassroomId],
        learningModeConfig: updatedLearningState,
      },
    };
  }

  if (type === ACTIONS.DELETE_LEARNING_MODE) {
    const { teacherClassroomId } = payload;

    return {
      [teacherClassroomId]: {
        ...state[teacherClassroomId],
        learningModeConfig: undefined,
      },
    };
  }

  if (type === ACTIONS.UPDATE_TEACHER_AV_STATE) {
    const { teacherClassroomId, ...avState } = payload;

    return {
      [teacherClassroomId]: {
        ...state[teacherClassroomId],
        teacherAVState: avState,
      },
    };
  }

  return state;
};

export default liveClassConfigReducer;
